/**
 * Shared camera behaviour for the two capture screens — the driver's
 * at-scene camera and the dispatcher's PhotoShop yard camera.
 *
 * Both grab frames from a `getUserMedia` preview rather than handing off to
 * the phone's camera app, so neither one leaves copies in the photo library
 * for the operator to delete later.
 */

const MAX_EDGE_PX = 1600;
const JPEG_QUALITY = 0.85;

/**
 * Where the thumb is.
 *
 * Held upright, the bottom-centre is already under the thumb. Turned
 * sideways it isn't — the hand is on the phone's bottom edge, which is now
 * one of the side edges, so the shutter follows it there and sits halfway up.
 *
 * Side comes from the legacy `window.orientation` where it exists, because
 * iOS Safari reports `screen.orientation.angle` as the reverse of Android
 * (WebKit #254863) and would otherwise put the button on the far side from
 * the hand on iPhones. Both spellings agree on meaning: +90 leaves the
 * device's bottom edge on the right, -90 / 270 on the left.
 *
 * Landscape itself comes from a media query, never the angle — angle alone
 * can't tell you the phone is sideways, which is the trap an earlier
 * rotation fix fell into.
 */
export const readOrientation = () => {
  const landscape = typeof window !== "undefined"
    && window.matchMedia?.("(orientation: landscape)").matches;
  const angle = typeof window.orientation === "number"
    ? window.orientation
    : (window.screen?.orientation?.angle ?? 0);
  return {
    landscape: Boolean(landscape),
    side: angle === -90 || angle === 270 ? "left" : "right",
  };
};

/**
 * Subscribe to rotation. Three listeners, all of them needed: a 90-to-270
 * flip changes no dimensions at all, so only `orientationchange` fires for
 * that one, while `resize` catches desktop window drags.
 */
export const watchOrientation = (onChange) => {
  const mq = window.matchMedia?.("(orientation: landscape)");
  mq?.addEventListener?.("change", onChange);
  window.addEventListener("orientationchange", onChange);
  window.addEventListener("resize", onChange);
  return () => {
    mq?.removeEventListener?.("change", onChange);
    window.removeEventListener("orientationchange", onChange);
    window.removeEventListener("resize", onChange);
  };
};

/**
 * The frame the operator is actually looking at.
 *
 * The video is `object-cover`, so a landscape sensor feed held upright shows
 * a cropped centre strip — they frame a vehicle in that strip, and saving the
 * whole wide frame instead is what came back as "the photo is sideways": a
 * portrait shot that arrived landscape. Crop the source to the viewfinder's
 * own aspect ratio and save precisely what they saw.
 *
 * Deliberately NOT rotating anything, after THREE failed attempts (Nov 2026).
 * Two attempts to "correct" it from pixel dimensions both made things worse:
 *   1. Rotating on `screen.orientation.angle` — angle 0 means the device's
 *      NATURAL orientation, which is landscape on plenty of tablets, so this
 *      turned every photo on its side on the phones that were already fine.
 *   2. Rotating when the frame's aspect disagreed with the viewfinder's.
 *      That disagreement is the ORDINARY state of a landscape feed inside a
 *      tall preview — exactly what the crop above exists to handle — so it
 *      fired on normal portrait shots and rotated photos that were upright.
 *      Reported as "mostly coming out 90 degrees out, sideways".
 *
 * The lesson every time: pixel dimensions cannot tell you whether a frame
 * needs rotating. A wide frame in a tall window and a genuinely sideways
 * frame are indistinguishable to any geometry test.
 *
 *   3. (3 Nov 2026) Rotating by the device's own `VideoFrame.rotation`.
 *      Sounder than the first two — it is a fact, not a deduction — but it
 *      was applied to a camera whose REAL fault was a 27 Oct bump to
 *      2560x1440, a Samsung stream mode that hands back un-rotated frames.
 *      The dispatcher's camera never left 1080p and has never once produced
 *      a sideways photo on the same phones, which is the proof that these
 *      devices need no correction at all. Reverting the resolution is the
 *      fix; the rotation was an extra variable in an already murky bug, and
 *      drivers reported photos sideways "whenever they rotate the phone in
 *      their hand" while it was live. Removed.
 *
 * So: nothing here rotates anything. The device's rotation IS recorded with
 * each driver capture (see `readTrackRotation`, diagnostic only) so the next
 * bad photo can be explained from evidence, and the per-photo rotate control
 * in the gallery remains the escape hatch for one-off oddities.
 */
const framedCrop = (vw, vh, box) => {
  if (!box?.width || !box?.height) return { sx: 0, sy: 0, sw: vw, sh: vh };
  const scale = Math.max(box.width / vw, box.height / vh);   // object-cover
  const sw = Math.min(vw, box.width / scale);
  const sh = Math.min(vh, box.height / scale);
  return { sx: (vw - sw) / 2, sy: (vh - sh) / 2, sw, sh };
};

/**
 * DIAGNOSTIC ONLY — never applied to a pixel.
 *
 * Reports what the device says about how its frames are turned
 * (`VideoFrame.rotation`, 0/90/180/270). Recorded alongside each driver
 * capture so a sideways photo can be explained from evidence instead of
 * another theory. It was briefly USED to rotate captures on 3 Nov 2026 and
 * that was a mistake: the dispatcher's camera runs this same helper-free
 * path at 1080p and is flawless on the very same Samsung phones, so these
 * devices need no correction at all. Applying one only added a second
 * variable to a bug we were already struggling to pin down.
 *
 * Probes a `track.clone()` which is then stopped, so the live preview's own
 * sink is never touched — attaching a second reader to the rendered track
 * can stall it on some Chrome builds. 600ms race-timeout; anything missing
 * or thrown reports 0. Safari has no MediaStreamTrackProcessor.
 *
 * DO NOT wire this into captureFrame without hard evidence from a real
 * device that its frames actually need turning.
 */
export const readTrackRotation = async (track, { timeoutMs = 600 } = {}) => {
  const Processor = typeof window !== "undefined" && window.MediaStreamTrackProcessor;
  if (!Processor || !track?.clone) return 0;
  let probe = null;
  let reader = null;
  try {
    probe = track.clone();
    reader = new Processor({ track: probe }).readable.getReader();
    const first = await Promise.race([
      reader.read(),
      new Promise((resolve) => setTimeout(() => resolve(null), timeoutMs)),
    ]);
    const frame = first?.value;
    const rotation = Number(frame?.rotation) || 0;
    frame?.close?.();
    return [90, 180, 270].includes(rotation) ? rotation : 0;
  } catch {
    return 0;
  } finally {
    try { await reader?.cancel(); } catch { /* already closed */ }
    probe?.stop();
  }
};

/**
 * Grab the current frame as a JPEG, cropped to the viewfinder and capped at
 * 1600px on its longest edge.
 *
 * The cap matters: a raw full-sensor frame is a 2-4MB upload per shot off a
 * phone in a yard, against roughly 200KB here, and the backend shrinks it to
 * this size anyway. Same 1600 / q0.85 as `imageResize.js`, so a photo taken
 * here and one picked from a file are indistinguishable to an insurer.
 */
export const captureFrame = async (video, { maxEdge = MAX_EDGE_PX, quality = JPEG_QUALITY } = {}) => {
  if (!video?.videoWidth) return null;
  const { sx, sy, sw, sh } = framedCrop(video.videoWidth, video.videoHeight, video.getBoundingClientRect());
  const scale = Math.min(1, maxEdge / Math.max(sw, sh));
  const w = Math.round(sw * scale);
  const h = Math.round(sh * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d").drawImage(video, sx, sy, sw, sh, 0, 0, w, h);
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), "image/jpeg", quality);
  });
};
